import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Pencil, Trash2, Search, Tag, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  addersService,
  type Adder,
  type AdderPricingModel,
} from '@/services/addersService';
import { AdderForm } from './AdderForm';
import { formatMoneyDisplay, tryDecimal } from '@/lib/money';

const PRICING_MODEL_LABELS: Record<AdderPricingModel, string> = {
  quote: 'Quote required',
  fixed: 'One-time charge',
  per_unit: 'Price per unit',
  per_watt: 'Price per watt',
  per_foot: 'Price per foot',
};

function formatPriceCell(adder: Adder): string {
  if (adder.pricing_model === 'quote') return 'Quote required';
  // ⚠️ SAME VIEWER-LOCALE DEFECT AS ProjectAddersSection.tsx, AND THIS IS ITS
  // SETTINGS-SIDE TWIN. `toLocaleString(undefined, ...)` takes the runtime's
  // locale, so a de-DE viewer read `$1.234,50` where a US viewer read
  // `$1,234.50` for the same adder. Two screens showing one catalogue, and the
  // separators mean opposite things on each.
  const parsed = tryDecimal(adder.unit_price);
  if (parsed === null) return '—';
  const price = formatMoneyDisplay(parsed);
  switch (adder.pricing_model) {
    case 'fixed':
      return price;
    case 'per_watt':
      return `${price} / watt`;
    case 'per_foot':
      return `${price} / ft`;
    case 'per_unit':
      return `${price} / ${adder.unit_label?.trim() || 'unit'}`;
    default:
      return price;
  }
}

export function AddersTab() {
  const { toast } = useToast();
  // useToast() returns a fresh `toast` function on every render, so route it
  // through a ref to keep loadAdders stable. Without this, useEffect re-fires
  // on every render and the loading spinner resets forever.
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [adders, setAdders] = useState<Adder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Adder | null>(null);
  const [deleting, setDeleting] = useState<Adder | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadAdders = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await addersService.listAdders({ includeInactive: true });
      setAdders(data);
    } catch (err) {
      toastRef.current({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to load adders.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdders();
  }, [loadAdders]);

  const categories = useMemo(() => {
    const seen = new Set<string>();
    for (const a of adders) {
      if (a.category && a.category.trim()) seen.add(a.category.trim());
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [adders]);

  const filteredAdders = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return adders;
    return adders.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.category || '').toLowerCase().includes(q) ||
        PRICING_MODEL_LABELS[a.pricing_model].toLowerCase().includes(q),
    );
  }, [adders, search]);

  // Group by category for the table — uncategorized go last under "Uncategorized".
  const grouped = useMemo(() => {
    const groups = new Map<string, Adder[]>();
    for (const a of filteredAdders) {
      const key = a.category?.trim() || 'Uncategorized';
      const list = groups.get(key) ?? [];
      list.push(a);
      groups.set(key, list);
    }
    // Sort categories alphabetically, but always put "Uncategorized" last.
    const sorted = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [filteredAdders]);

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = (adder: Adder) => {
    setEditing(adder);
    setShowForm(true);
  };

  const handleSave = async (values: {
    name: string;
    category: string | null;
    pricing_model: AdderPricingModel;
    unit_price: number | null;
    unit_label: string | null;
    is_active: boolean;
  }) => {
    setIsSaving(true);
    try {
      if (editing) {
        await addersService.updateAdder(editing.id, values);
        toast({ title: 'Adder updated', description: `"${values.name}" was updated.` });
      } else {
        await addersService.createAdder(values);
        toast({ title: 'Adder added', description: `"${values.name}" was added.` });
      }
      setShowForm(false);
      setEditing(null);
      await loadAdders();
    } catch (err) {
      toast({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to save adder.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleting) return;
    setIsSaving(true);
    try {
      await addersService.deleteAdder(deleting.id);
      toast({ title: 'Adder deleted', description: `"${deleting.name}" was removed.` });
      setDeleting(null);
      await loadAdders();
    } catch (err) {
      toast({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Failed to delete adder.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Adders</h2>
          <p className="text-sm text-muted-foreground">
            Manage the catalog of adders your team can apply to projects. Each adder
            can be priced as a quote, one-time charge, or per unit / watt / foot.
          </p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4 mr-2" />
          Add Adder
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search adders..."
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredAdders.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-lg">
          {search
            ? 'No adders match your search.'
            : 'No adders yet. Click "Add Adder" to create your first one.'}
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([category, items]) => (
            <div key={category} className="space-y-2">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold text-foreground">{category}</h3>
                <Badge variant="secondary" className="text-xs">
                  {items.length}
                </Badge>
              </div>
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Pricing Model</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[120px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((adder) => (
                      <TableRow key={adder.id}>
                        <TableCell className="font-medium">{adder.name}</TableCell>
                        <TableCell>
                          {PRICING_MODEL_LABELS[adder.pricing_model]}
                        </TableCell>
                        <TableCell>{formatPriceCell(adder)}</TableCell>
                        <TableCell>
                          {adder.is_active ? (
                            <Badge variant="default" className="bg-primary/10 text-primary hover:bg-primary/15">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(adder)}
                              aria-label={`Edit ${adder.name}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleting(adder)}
                              aria-label={`Delete ${adder.name}`}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit form */}
      <AdderForm
        open={showForm}
        adder={editing}
        existingCategories={categories}
        isSaving={isSaving}
        onCancel={() => {
          setShowForm(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete adder?</DialogTitle>
            <DialogDescription>
              This will permanently delete "{deleting?.name}" from your catalog.
              Projects that already use it will keep their snapshot, but the
              adder will no longer appear when adding new ones.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
